import { Injectable, computed, signal } from '@angular/core';

import { CompatTarget, CompatibilityRequirement } from '@core/filters/path-filter/wire';
import { CodedStatusMessage } from '@core/models/coded-status-message.model';
import { JsonSchema } from '@core/models/transformers.model';

export interface CompatibilityEditRequest {
  /** The carrier's root schema, which holds the `x-compatibility-*` extensions. */
  carrier: JsonSchema;
  /** The carrier's display name, offered by `self` value slots ("This job's field"). */
  selfLabel: string;
  target: CompatTarget;
  /** The stored requirement under edit; null when adding one. */
  requirement: CompatibilityRequirement | null;
  onApply: (requirement: CompatibilityRequirement) => void;
}

/**
 * The requirement cards live deep inside the generic form builder, while the panel that edits a
 * requirement docks beside the form on the entity edit page. The session carries a requirement
 * from a card to the panel and the applied result back, and keeps the last refused save for the
 * cards to show.
 */
@Injectable({ providedIn: 'root' })
export class CompatibilityEditSessionService {
  private readonly requestState = signal<CompatibilityEditRequest | null>(null);
  private readonly saveErrorState = signal<CodedStatusMessage | null>(null);

  readonly request = this.requestState.asReadonly();
  readonly isActive = computed(() => this.requestState() !== null);
  readonly saveError = this.saveErrorState.asReadonly();

  begin(request: CompatibilityEditRequest): void {
    this.requestState.set(request);
  }

  apply(requirement: CompatibilityRequirement): void {
    const request = this.requestState();
    this.requestState.set(null);
    request?.onApply(requirement);
  }

  end(): void {
    this.requestState.set(null);
  }

  setSaveError(error: CodedStatusMessage | null): void {
    this.saveErrorState.set(error);
  }
}
